import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme, keyframes } from '@mui/material/styles';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import StarIcon from '@mui/icons-material/Star';

const IOS_URL = 'https://apps.apple.com/us/app/dr-bardawam/id6783860943';
const ANDROID_URL =
  'https://play.google.com/store/apps/details?id=com.drbardawam.customers';

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-12px) rotate(2deg); }
  66% { transform: translateY(-6px) rotate(-1deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

function FloatingOrb({ size, top, left, right, delay, color }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(color, 0.3)}, transparent 70%)`,
        top,
        left,
        right,
        animation: `${pulse} ${4 + delay}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        pointerEvents: 'none',
      }}
    />
  );
}

function StoreButton({ icon: Icon, storeName, storeLabel, url, navy }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const bg = navy
    ? isDark
      ? 'linear-gradient(135deg, #0d0d14 0%, #12182a 50%, #0a2540 100%)'
      : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
    : 'linear-gradient(135deg, #8F6A35 0%, #B8894A 50%, #D4A574 100%)';

  const glowColor = navy ? (isDark ? '#1a3a5c' : '#0f3460') : '#B8894A';

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="contained"
      size="large"
      sx={{
        flex: 1,
        minWidth: { xs: '100%', sm: 260 },
        maxWidth: 340,
        py: { xs: 2.5, sm: 3 },
        px: 4,
        borderRadius: 4,
        background: bg,
        color: '#fff',
        textTransform: 'none',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        boxShadow: `0 8px 32px ${alpha(glowColor, isDark ? 0.5 : 0.35)}`,
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.12) 45%, transparent 50%)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 3s ease-in-out infinite`,
          animationDelay: navy ? '0s' : '1.5s',
        },
        '&:hover': {
          transform: 'translateY(-5px) scale(1.03)',
          boxShadow: `0 16px 48px ${alpha(glowColor, isDark ? 0.6 : 0.45)}`,
          background: bg,
        },
        '&:active': {
          transform: 'translateY(-2px) scale(1.01)',
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 30, color: '#fff' }} />
        </Box>
        <Stack alignItems="flex-start" spacing={0.25}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, fontSize: 11, letterSpacing: 0.5 }}
          >
            {storeLabel}
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            fontWeight={800}
            sx={{ lineHeight: 1.2 }}
          >
            {storeName}
          </Typography>
        </Stack>
      </Stack>
    </Button>
  );
}

function PhoneMockup({ isDark }) {
  const glassBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.2)';
  const glassBg = isDark
    ? 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.03))'
    : 'linear-gradient(145deg, rgba(255,255,255,0.25), rgba(255,255,255,0.05))';

  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 180, sm: 200, md: 220 },
        height: { xs: 340, sm: 380, md: 420 },
        mx: 'auto',
        animation: `${float} 6s ease-in-out infinite`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '36px',
          background: glassBg,
          backdropFilter: 'blur(20px)',
          border: `2px solid ${glassBorder}`,
          boxShadow: isDark
            ? '0 30px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)'
            : '0 30px 60px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            width: 60,
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.3)',
          }}
        />

        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '18px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))'
              : 'linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}
        >
          <Box
            component="img"
            src="/logo512.png"
            alt="Crystal Bardawam"
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              objectFit: 'cover',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML =
                '<span style="color:#fff;font-weight:900;font-size:22px">CB</span>';
            }}
          />
        </Box>

        <Typography
          variant="body2"
          fontWeight={800}
          sx={{ color: '#fff', mb: 0.5 }}
        >
          Crystal Bardawam
        </Typography>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {[1, 2, 3, 4, 5].map((s) => (
            <StarIcon key={s} sx={{ fontSize: 14, color: '#FFD700' }} />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

export default function DownloadApp() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const waveBg = isDark ? 'background.paper' : '#FBF7F0';
  const heroGradient = isDark
    ? 'linear-gradient(165deg, #1A140C 0%, #2C2416 22%, #4A3720 48%, #6B4F28 72%, #8F6A35 100%)'
    : 'linear-gradient(165deg, #4A3518 0%, #6B4F28 20%, #B8894A 50%, #D4A574 80%, #E8C878 100%)';
  const ctaGradient = isDark
    ? 'linear-gradient(135deg, #3D2E18 0%, #6B4F28 45%, #8F6A35 100%)'
    : 'linear-gradient(135deg, #6B4F28 0%, #B8894A 50%, #D4A574 100%)';

  return (
    <Box sx={{ bgcolor: 'background.default', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'relative',
          background: heroGradient,
          pt: { xs: 5, md: 8 },
          pb: { xs: 10, md: 14 },
          px: 2,
          overflow: 'hidden',
        }}
      >
        <FloatingOrb size={300} top="-10%" left="-5%" delay={0} color={isDark ? '#B8894A' : '#E8C878'} />
        <FloatingOrb size={200} top="20%" right="-3%" delay={1.5} color={isDark ? '#D4A574' : '#fff'} />
        <FloatingOrb size={150} top="60%" left="10%" delay={3} color="#D4A574" />
        <FloatingOrb size={100} top="10%" right="25%" delay={2} color={isDark ? '#B8894A' : '#fff'} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            spacing={{ xs: 4, md: 6 }}
          >
            <Stack
              alignItems={{ xs: 'center', md: 'flex-start' }}
              textAlign={{ xs: 'center', md: 'start' }}
              spacing={3}
              sx={{ flex: 1, maxWidth: { md: 560 } }}
            >
              <Chip
                icon={<SmartphoneIcon sx={{ fontSize: 18, color: '#fff !important' }} />}
                label={t('download_app_badge')}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  px: 1,
                  height: 36,
                }}
              />

              <Typography
                variant={isSmall ? 'h4' : isMobile ? 'h3' : 'h2'}
                fontWeight={900}
                lineHeight={1.25}
                sx={{ color: '#fff' }}
              >
                {t('download_app_title')}
              </Typography>

              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.88)' : '#FFFFFF',
                  maxWidth: 480,
                  fontWeight: 600,
                  lineHeight: 1.9,
                }}
              >
                {t('download_app_subtitle')}
              </Typography>
            </Stack>

            <PhoneMockup isDark={isDark} />
          </Stack>
        </Container>

        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            left: 0,
            right: 0,
            height: { xs: 60, md: 80 },
            bgcolor: waveBg,
            borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
            opacity: isDark ? 0.92 : 1,
          }}
        />
      </Box>

      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 5,
              background: ctaGradient,
              p: { xs: 4, md: 5 },
              textAlign: 'center',
              border: isDark ? '1px solid rgba(212,165,116,0.18)' : 'none',
              boxShadow: isDark
                ? '0 16px 40px rgba(0,0,0,0.45)'
                : '0 12px 32px rgba(184,137,74,0.2)',
            }}
          >
            <FloatingOrb size={200} top="-30%" right="-5%" delay={0} color="#fff" />
            <FloatingOrb size={120} top="50%" left="-5%" delay={2} color="#E8C878" />

            <Stack alignItems="center" spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  lineHeight: 1.8,
                  textAlign: 'center',
                }}
              >
                {t('download_app_hint')}
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems="center"
                sx={{ pt: 1 }}
              >
                <StoreButton
                  icon={AppleIcon}
                  storeName="App Store"
                  storeLabel={t('download_get_it_on')}
                  url={IOS_URL}
                  navy
                />
                <StoreButton
                  icon={AndroidIcon}
                  storeName="Google Play"
                  storeLabel={t('download_get_it_on')}
                  url={ANDROID_URL}
                />
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
